import React, { useCallback, useRef, useState } from "react"
import XGSSelect, { XGSSelectOption } from "../../../xgs-select/xgsSelect";

export interface SelectColumnFilterProps {
    column: any
}

const defaultSortFn = (item1: any, item2: any) => item1 > item2 ? 1 : -1;

/** Sub-pixel rounding of the control position must not be treated as scrolling. */
const ANCHOR_DRIFT_TOLERANCE_PX = 1;

export const SelectColumnFilter: React.FC<SelectColumnFilterProps> = ({
    column
}) => {
  const { filterLabelFormatter, filterSortFn } = column.columnDef.meta ?? {};

  const [value, setValue] = useState<XGSSelectOption | undefined>(() => {
    const fv = column.getFilterValue();
    return fv != null && fv !== ""
      ? { value: String(fv), label: filterLabelFormatter ? filterLabelFormatter(fv) : String(fv) }
      : undefined;
  });

  const containerRef = useRef<HTMLDivElement | null>(null);
  const anchorPositionRef = useRef<{ left: number; top: number } | null>(null);

  const getControlPosition = () => {
    if (!containerRef.current) return null;
    const { left, top } = containerRef.current.getBoundingClientRect();
    return { left, top };
  };

  const onMenuOpen = useCallback(() => {
    anchorPositionRef.current = getControlPosition();
  }, []);

  const onMenuClose = useCallback(() => {
    anchorPositionRef.current = null;
  }, []);

  const closeMenuOnScroll = useCallback((e: Event) => {
    const target = e.target as HTMLElement | Document | null;
    // Keep open while scrolling the options list itself
    if (target instanceof HTMLElement && target.closest(".xgs-select__menu")) {
      return false;
    }
    // The menu is portaled and fixed-positioned, so it detaches from the input
    // as soon as scrolling (e.g. horizontally through the columns) moves the control
    const anchorPosition = anchorPositionRef.current;
    const currentPosition = getControlPosition();
    if (
      anchorPosition &&
      currentPosition &&
      (Math.abs(currentPosition.left - anchorPosition.left) > ANCHOR_DRIFT_TOLERANCE_PX ||
        Math.abs(currentPosition.top - anchorPosition.top) > ANCHOR_DRIFT_TOLERANCE_PX)
    ) {
      return true;
    }
    // Keep open when the datatable scrolls without moving the header (e.g. vertical row scroll)
    if (target instanceof HTMLElement && target.closest(".xgs-data-table__wrapper")) {
      return false;
    }
    return true;
  }, []);

  const options: XGSSelectOption[] = [];

  new Map<string, number>([...column.getFacetedUniqueValues().entries()].sort(filterSortFn || defaultSortFn))
    .forEach((count, key) => {
      // equalsString auto-removes falsey/empty filter values, so blank facet keys never filter
      const optionValue = key == null ? "" : String(key);
      if (optionValue.trim() === "") return;
      options.push({
        value: optionValue,
        label: `${filterLabelFormatter ? filterLabelFormatter(key) : optionValue} (${count})`,
      });
    });

  return (
    <div ref={containerRef}>
      <XGSSelect
        id='column-filter-select'
        value={value}
        options={options}
        disabled={false}
        onValueChange={(value) => {
          column.setFilterValue(value?.value ?? undefined);
          setValue(value ?? undefined);
        }}
        clearable
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 1100 }),
        }}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        closeMenuOnScroll={closeMenuOnScroll}
      />
    </div>
  );
};
